import React from 'react';
import { Badge, Box, Heading } from '@razorpay/blade/components';
import { useFormWithProvider } from 'forms';
import useFeature from 'hooks/useFeature';
import {
  ReviewSection,
  ReviewSectionContainer,
  ReviewSectionHeader,
  ReviewSectionItem,
  ReviewSectionItemContainer,
} from 'components/WizardViews/components/review';
import { FlexibleBenefitFormData } from './types';

interface ReviewStepProps {
  onEditBasicDetails: () => void;
  onEditAmountDetails: () => void;
  onEditTaxDetails: () => void;
}

export const ReviewStep = ({
  onEditBasicDetails,
  onEditAmountDetails,
  onEditTaxDetails,
}: ReviewStepProps) => {
  const { formData } = useFormWithProvider<FlexibleBenefitFormData>();
  const { isBaseCompensationFloorEnabled } = useFeature('baseCompensationFloor');

  const formatBenefitAmount = () => {
    const settings = formData.benefit_amount_settings;
    const benefitAmountType = settings?.type;

    if (benefitAmountType === 'FIXED' || benefitAmountType === 'RANGE') {
      if (settings?.unit === 'ACTUAL_AMOUNT') {
        return `Max Limit - ₹${settings.value || 0}`;
      } else if (settings?.unit === 'PERCENTAGE_OF_BASIC') {
        return `Max Limit - ${settings.value || 0}% of Basic`;
      }
    } else if (benefitAmountType === 'LIST_OF_VALUES') {
      const options = settings?.options || [];
      if (options.length === 0) return 'No values selected';

      return (
        <Box display="flex" gap="spacing.2" flexWrap="wrap">
          {options.map((option: string | number, index: number) => (
            <Badge key={index} color="neutral" emphasis="subtle">
              ₹{option}
            </Badge>
          ))}
        </Box>
      );
    } else if (!Boolean(formData.declaration_settings?.declaration_enabled)) {
      return `₹${settings?.value || 0}`;
    }

    return 'Not set';
  };

  const formatDeclarationMethod = () => {
    if (formData.declaration_settings?.declaration_enabled === 'true') {
      return 'Allow declaration';
    } else {
      return 'Auto-apply full amount';
    }
  };

  const formatBenefitType = () => {
    return formData.nonCashBenefit ? 'Non-cash benefit' : 'Cash benefit';
  };

  const formatUnclaimedAmount = () => {
    if (formData.hold_settings?.hold_unclaimed_amount === 'true') {
      return 'Hold';
    } else if (formData.hold_settings?.hold_unclaimed_amount === 'false') {
      return 'Release';
    }
    return 'Not set';
  };

  return (
    <Box padding="spacing.6" display="flex" flexDirection="column" gap="spacing.6">
      <Box>
        <Heading size="large" weight="semibold" color="surface.text.gray.normal">
          Review
        </Heading>
      </Box>

      <ReviewSectionContainer>
        <ReviewSection>
          <ReviewSectionHeader title="Basic" subtitle="details" onEditClick={onEditBasicDetails} />
          <ReviewSectionItemContainer>
            <ReviewSectionItem
              label="Component Name"
              value={formData.name || 'Not set'}
              status={formData.name ? 'success' : 'error'}
            />
            <ReviewSectionItem
              label="Payslip & Salary Register name"
              value={formData.displayName || 'Not set'}
              status={formData.displayName ? 'success' : 'error'}
            />
            {formData.description && (
              <ReviewSectionItem
                label="Component Description"
                value={formData.description}
                status="success"
              />
            )}
            <ReviewSectionItem
              label="Include in Gross Salary"
              value={formData.showInGrossSalary ? 'Yes' : 'No'}
              status="success"
            />
            <ReviewSectionItem label="Benefit type" value={formatBenefitType()} status="success" />
          </ReviewSectionItemContainer>
        </ReviewSection>

        {/* Amount Details Section */}
        <ReviewSection>
          <ReviewSectionHeader
            title="Amount"
            subtitle="details"
            onEditClick={onEditAmountDetails}
          />
          <ReviewSectionItemContainer>
            <ReviewSectionItem
              label="Declaration Mode"
              value={formatDeclarationMethod()}
              status="success"
            />
            <ReviewSectionItem
              label="Benefit amount"
              value={formatBenefitAmount()}
              status={
                formData.benefit_amount_settings?.value ||
                (formData.benefit_amount_settings?.options?.length ?? 0) > 0
                  ? 'success'
                  : 'error'
              }
            />
          </ReviewSectionItemContainer>
        </ReviewSection>

        <ReviewSection>
          <ReviewSectionHeader
            title="Taxability"
            subtitle="details"
            onEditClick={onEditTaxDetails}
          />
          <ReviewSectionItemContainer>
            <ReviewSectionItem
              label="Tax exempted in Old Regime"
              value={formData.tax_behaviour?.tax_exempt_in_old_regime ? 'Yes' : 'No'}
              status="success"
            />
            <ReviewSectionItem
              label="Tax exempted in New Regime"
              value={formData.tax_behaviour?.tax_exempt_in_new_regime ? 'Yes' : 'No'}
              status="success"
            />
            <ReviewSectionItem
              label="Proration"
              value={
                formData.tax_behaviour?.tax_deduction_behaviour === 'pro_rated'
                  ? 'Enabled'
                  : 'Disabled'
              }
              status="success"
            />
            <ReviewSectionItem
              label="Arrear calculation"
              value={formData.arrear_behaviour?.consider_in_arrear ? 'Enabled' : 'Disabled'}
              status="success"
            />
            <ReviewSectionItem
              label="Unclaimed amount"
              value={formatUnclaimedAmount()}
              status={formData.hold_settings?.hold_unclaimed_amount ? 'success' : 'error'}
            />
          </ReviewSectionItemContainer>
        </ReviewSection>

        {formData.showInGrossSalary && (
          <ReviewSection>
            <ReviewSectionHeader
              title="Wage calculation"
              subtitle="details"
              onEditClick={onEditTaxDetails}
            />
            <ReviewSectionItemContainer>
              <ReviewSectionItem
                label="Part of PF wage"
                value={formData.pf_behaviour?.include_in_pf_wages ? 'Yes' : 'No'}
                status="success"
              />
              <ReviewSectionItem
                label="Part of ESI wage"
                value={formData.esi_behaviour?.include_in_esi_wages ? 'Yes' : 'No'}
                status="success"
              />
              <ReviewSectionItem
                label="Part of PT wage"
                value={formData.pt_behaviour?.include_in_pt_wages ? 'Yes' : 'No'}
                status="success"
              />
              <ReviewSectionItem
                label="Part of LWF wage"
                value={formData.lwf_behaviour?.include_in_lwf_wages ? 'Yes' : 'No'}
                status="success"
              />
              <ReviewSectionItem
                label="Part of Gratuity wages"
                value={formData.gratuity_behaviour?.include_in_gratuity_wages ? 'Yes' : 'No'}
                status="success"
              />
              <ReviewSectionItem
                label="Part of Leave Encashment wages"
                value={
                  formData.leave_encashment_behaviour?.include_in_leave_encashment_wages
                    ? 'Yes'
                    : 'No'
                }
                status="success"
              />
              {isBaseCompensationFloorEnabled && (
                <ReviewSectionItem
                  label="Part of Base Compensation"
                  value={
                    formData.base_compensation_behaviour?.include_in_base_compensation
                      ? 'Yes'
                      : 'No'
                  }
                  status="success"
                />
              )}
            </ReviewSectionItemContainer>
          </ReviewSection>
        )}
      </ReviewSectionContainer>
    </Box>
  );
};
